"use client";

import { EditorContent, useEditor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import { Extension } from "@tiptap/core";
import { Decoration, DecorationSet } from "@tiptap/pm/view";
import { Plugin, PluginKey } from "@tiptap/pm/state";
import type { CandidateSpan } from "@fluentry/domain";
import { useEffect, useMemo, useRef } from "react";
import { isSubmitShortcut } from "./workbench-core.helpers";
import type { PopoverAnchorRect } from "./candidate-popover";

type CandidateEditorProps = {
  document: string;
  visibleSpans: CandidateSpan[];
  activeSpanId: string | null;
  onDocumentChange: (nextDocument: string) => void;
  onSelectionRangeChange: (range: { start: number; end: number } | null) => void;
  onSpanClick: (spanId: string | null, anchorRect: PopoverAnchorRect | null) => void;
  onSpanPreviewChange?: (spanId: string | null, anchorRect: PopoverAnchorRect | null) => void;
  onSubmitShortcut?: () => void;
  ariaLabel?: string;
  placeholder?: string;
};

const decorationPluginKey = new PluginKey("candidate-decorations");

export function CandidateEditor({
  document,
  visibleSpans,
  activeSpanId,
  onDocumentChange,
  onSelectionRangeChange,
  onSpanClick,
  onSpanPreviewChange,
  onSubmitShortcut,
  ariaLabel = "Draft editor",
  placeholder = "Type or paste your text here...",
}: CandidateEditorProps) {
  const visibleSpansRef = useRef(visibleSpans);
  const activeSpanIdRef = useRef(activeSpanId);
  const skipUpdateRef = useRef(false);

  visibleSpansRef.current = visibleSpans;
  activeSpanIdRef.current = activeSpanId;

  const candidateDecorations = useMemo(
    () =>
      Extension.create({
        name: "candidateDecorations",
        addProseMirrorPlugins() {
          return [
            new Plugin({
              key: decorationPluginKey,
              props: {
                decorations: (state) =>
                  DecorationSet.create(
                    state.doc,
                    visibleSpansRef.current.map((span) =>
                      Decoration.inline(
                        offsetToPos(state.doc, span.start),
                        offsetToPos(state.doc, span.end),
                        {
                          // Decoration markup is generated outside JSX, so inline styles keep highlight visibility stable even when utility extraction misses these spans.
                          style:
                            span.id === activeSpanIdRef.current
                              ? "background-color:#bfe9e5;border-radius:0.125rem;box-shadow:inset 0 0 0 1px #5eb8ae;"
                              : "background-color:#dce9e6;border-radius:0.125rem;box-shadow:inset 0 0 0 1px rgba(168,199,194,0.8);",
                          "data-candidate-span-id": span.id,
                          tabindex: "0",
                          role: "button",
                          "aria-haspopup": "dialog",
                        },
                      ),
                    ),
                  ),
              },
            }),
          ];
        },
      }),
    [],
  );

  const editor = useEditor({
    immediatelyRender: false,
    extensions: [
      StarterKit.configure({
        blockquote: false,
        bulletList: false,
        codeBlock: false,
        code: false,
        dropcursor: false,
        gapcursor: false,
        heading: false,
        horizontalRule: false,
        orderedList: false,
      }),
      candidateDecorations,
    ],
    content: textToDoc(document),
    editorProps: {
      attributes: {
        "aria-label": ariaLabel,
        "aria-multiline": "true",
        class:
          "min-h-[240px] min-w-0 rounded-[1.45rem] bg-transparent px-4 py-4 text-base leading-7 text-slate-900 outline-none sm:min-h-[320px] sm:px-5 sm:py-5 sm:text-[1.05rem] sm:leading-8",
        role: "textbox",
      },
      handleKeyDown: (_view, event) => {
        if (isSubmitShortcut(event)) {
          event.preventDefault();
          onSubmitShortcut?.();
          return true;
        }

        return false;
      },
    },
    onUpdate: ({ editor: nextEditor }) => {
      if (skipUpdateRef.current) {
        skipUpdateRef.current = false;
        return;
      }

      onDocumentChange(nextEditor.getText({ blockSeparator: "\n" }));
    },
    onSelectionUpdate: ({ editor: nextEditor }) => {
      onSelectionRangeChange(
        selectionToOffsets(
          nextEditor.state.doc,
          nextEditor.state.selection.from,
          nextEditor.state.selection.to,
        ),
      );
    },
  });

  useEffect(() => {
    if (!editor) {
      return;
    }

    const current = editor.getText({ blockSeparator: "\n" });
    if (current === document) {
      return;
    }

    skipUpdateRef.current = true;
    editor.commands.setContent(textToDoc(document), { emitUpdate: false });
  }, [document, editor]);

  useEffect(() => {
    if (!editor) {
      return;
    }

    editor.view.dispatch(editor.state.tr.setMeta("candidate-decoration-refresh", Date.now()));
  }, [activeSpanId, editor, visibleSpans]);

  useEffect(() => {
    if (!editor) {
      return;
    }

    onSelectionRangeChange(
      selectionToOffsets(editor.state.doc, editor.state.selection.from, editor.state.selection.to),
    );
  }, [editor, onSelectionRangeChange]);

  return (
    <div
      className="relative min-w-0 overflow-hidden rounded-[1.45rem] border border-[#0f766e]/10 bg-white shadow-[inset_0_1px_0_rgba(255,255,255,0.75)]"
      data-testid="workbench-draft-editor"
    >
      {editor?.isEmpty ? (
        <div className="pointer-events-none absolute top-4 left-4 text-sm leading-6 text-[#8aa1a3] sm:top-5 sm:left-5">
          {placeholder}
        </div>
      ) : null}
      <div
        onMouseOver={(event) => {
          const spanElement = findCandidateSpanElement(event.target);
          if (!spanElement) {
            return;
          }

          onSpanPreviewChange?.(
            spanElement.dataset.candidateSpanId ?? null,
            rectFromElement(spanElement),
          );
        }}
        onMouseOut={(event) => {
          const spanElement = findCandidateSpanElement(event.target);
          const relatedSpanElement = findCandidateSpanElement(event.relatedTarget);
          if (!spanElement || spanElement === relatedSpanElement) {
            return;
          }

          onSpanPreviewChange?.(null, null);
        }}
        onFocusCapture={(event) => {
          const spanElement = findCandidateSpanElement(event.target);
          if (!spanElement) {
            return;
          }

          onSpanPreviewChange?.(
            spanElement.dataset.candidateSpanId ?? null,
            rectFromElement(spanElement),
          );
        }}
        onBlurCapture={(event) => {
          const spanElement = findCandidateSpanElement(event.target);
          const relatedSpanElement = findCandidateSpanElement(event.relatedTarget);
          if (!spanElement || spanElement === relatedSpanElement) {
            return;
          }

          onSpanPreviewChange?.(null, null);
        }}
        onClick={(event) => {
          const spanElement = findCandidateSpanElement(event.target);
          if (!spanElement) {
            onSpanClick(null, null);
            return;
          }

          onSpanClick(spanElement.dataset.candidateSpanId ?? null, rectFromElement(spanElement));
        }}
        onKeyDown={(event) => {
          const spanElement = findCandidateSpanElement(event.target);
          if (!spanElement) {
            return;
          }

          if (event.key !== "Enter" && event.key !== " ") {
            return;
          }

          event.preventDefault();
          onSpanClick(spanElement.dataset.candidateSpanId ?? null, rectFromElement(spanElement));
        }}
      >
        <EditorContent editor={editor} />
      </div>
    </div>
  );
}

function textToDoc(value: string) {
  const content: Array<{ type: "text"; text: string } | { type: "hardBreak" }> = [];
  const lines = value.split("\n");
  lines.forEach((line, index) => {
    if (line.length > 0) {
      content.push({ type: "text", text: line });
    }
    if (index < lines.length - 1) {
      content.push({ type: "hardBreak" });
    }
  });

  return {
    type: "doc",
    content: [
      {
        type: "paragraph",
        content,
      },
    ],
  };
}

function offsetToPos(doc: Parameters<typeof DecorationSet.create>[0], offset: number) {
  if (offset <= 0) {
    return 1;
  }

  let consumed = 0;
  let resolvedPos = doc.content.size - 1;
  doc.descendants((node, pos) => {
    if (node.isText) {
      const length = node.text?.length ?? 0;
      if (offset <= consumed + length) {
        resolvedPos = pos + (offset - consumed);
        return false;
      }
      consumed += length;
      return;
    }

    if (node.type.name === "hardBreak") {
      if (offset <= consumed + 1) {
        resolvedPos = pos + Math.max(0, offset - consumed);
        return false;
      }
      consumed += 1;
    }
  });
  return resolvedPos;
}

function selectionToOffsets(
  doc: Parameters<typeof DecorationSet.create>[0],
  from: number,
  to: number,
) {
  return {
    start: posToOffset(doc, from),
    end: posToOffset(doc, to),
  };
}

function posToOffset(doc: Parameters<typeof DecorationSet.create>[0], targetPos: number) {
  let consumed = 0;
  let resolved = 0;

  doc.descendants((node, pos) => {
    if (node.isText) {
      const length = node.text?.length ?? 0;
      if (targetPos <= pos + length) {
        resolved = consumed + Math.max(0, targetPos - pos);
        return false;
      }
      consumed += length;
      resolved = consumed;
      return;
    }

    if (node.type.name === "hardBreak") {
      if (targetPos <= pos + 1) {
        resolved = consumed + Math.max(0, targetPos - pos);
        return false;
      }
      consumed += 1;
      resolved = consumed;
    }
  });

  return resolved;
}

function findCandidateSpanElement(target: EventTarget | null) {
  return target instanceof HTMLElement
    ? target.closest<HTMLElement>("[data-candidate-span-id]")
    : null;
}

function rectFromElement(element: HTMLElement): PopoverAnchorRect {
  const rect = element.getBoundingClientRect();
  return {
    top: rect.top,
    left: rect.left,
    width: rect.width,
    height: rect.height,
    bottom: rect.bottom,
    right: rect.right,
  };
}
